// ===========================================
// Navigation Settings Tab
// Phase 2 Priority 3 - Admin Settings Enhancements
// ===========================================

import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Button,
  Stack,
  Typography,
  Alert,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Dashboard as DashboardIcon,
  Mail as MessagesIcon,
  People as MeetAndGreetIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  Group as PeopleIcon,
  CalendarMonth as SchedulingIcon,
  Work as OperationsIcon,
  Assessment,
  Feed as ContentIcon,
  EmojiEvents as GamificationIcon,
  AdminPanelSettings as SystemIcon,
  QuestionAnswerOutlined as EnquiriesIcon,
  Celebration as EventsIcon,
  Save as SaveIcon,
  Refresh as ResetIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useAdminNavigation,
  useUpdateAdminNavigation,
  useResetAdminNavigation,
} from '../../../hooks/useAdminSettings';
import SettingsWriteGate from './SettingsWriteGate';

// ===========================================
// NAVIGATION ITEMS CONFIG
// ===========================================
// IMPORTANT: When adding/removing items here, also update adminNavigationConfig in
// components/layout/AdminLayout.tsx and the backend validNavItemIds in
// validators/adminSettings.validators.ts to keep them aligned.

const NAV_ITEMS: Record<string, { label: string; icon: React.ReactNode; description: string }> = {
  dashboard: {
    label: 'Dashboard',
    icon: <DashboardIcon />,
    description: 'Overview and quick stats',
  },
  messages: {
    label: 'Messages',
    icon: <MessagesIcon />,
    description: 'Internal messaging system',
  },
  calendar: {
    label: 'Calendar',
    icon: <SchedulingIcon />,
    description: 'View and manage class schedules',
  },
  meetAndGreet: {
    label: 'Meet & Greet',
    icon: <MeetAndGreetIcon />,
    description: 'Trial lesson bookings',
  },
  enquiries: {
    label: 'Enquiries',
    icon: <EnquiriesIcon />,
    description: 'Public enquiry form submissions',
  },
  events: {
    label: 'Events',
    icon: <EventsIcon />,
    description: 'Events & Save the Dates',
  },
  'school-setup': {
    label: 'School Setup',
    icon: <SchoolIcon />,
    description: 'Terms, locations, rooms',
  },
  'class-config': {
    label: 'Class Configuration',
    icon: <ClassIcon />,
    description: 'Age groups, durations, instruments, class types, program levels, streams, piece library',
  },
  gamification: {
    label: 'Gamification',
    icon: <GamificationIcon />,
    description: 'Achievements, student progress, leaderboard',
  },
  people: {
    label: 'People',
    icon: <PeopleIcon />,
    description: 'Teachers, families, students',
  },
  scheduling: {
    label: 'Scheduling',
    icon: <SchedulingIcon />,
    description: 'Classes and extra lesson requests',
  },
  operations: {
    label: 'Operations',
    icon: <OperationsIcon />,
    description: 'Invoices, subscriptions, class pricing, products, payment settings',
  },
  reports: {
    label: 'Reports',
    icon: <Assessment />,
    description: 'Analytics and reporting dashboard',
  },
  content: {
    label: 'Communications',
    icon: <ContentIcon />,
    description: 'Newsletters, custom emails, SMS & WhatsApp',
  },
  system: {
    label: 'System',
    icon: <SystemIcon />,
    description: 'Integrations, security audit, email settings, system settings',
  },
};

// ===========================================
// SORTABLE ITEM COMPONENT
// ===========================================

// Items that cannot be hidden (e.g., System contains the settings page itself)
const ALWAYS_VISIBLE_ITEMS = ['system'];

interface SortableItemProps {
  id: string;
  isHidden: boolean;
  onToggleVisibility: () => void;
}

function SortableItem({ id, isHidden, onToggleVisibility }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const item = NAV_ITEMS[id];
  if (!item) return null;

  const isAlwaysVisible = ALWAYS_VISIBLE_ITEMS.includes(id);

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        border: '1px solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
        borderRadius: 1,
        mb: 1,
        bgcolor: isDragging
          ? 'action.hover'
          : isHidden
          ? 'action.disabledBackground'
          : 'background.paper',
        opacity: isHidden ? 0.6 : 1,
      }}
    >
      <ListItemIcon
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab', minWidth: 40 }}
      >
        <DragIcon color="action" />
      </ListItemIcon>
      <ListItemIcon sx={{ minWidth: 40 }}>
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        secondary={isAlwaysVisible ? 'Always visible — contains system settings' : item.description}
      />
      <ListItemSecondaryAction>
        <Tooltip title={isAlwaysVisible ? 'System menu is always visible' : isHidden ? 'Show in navigation' : 'Hide from navigation'}>
          <span>
            <Switch
              edge="end"
              checked={!isHidden}
              onChange={onToggleVisibility}
              disabled={isAlwaysVisible}
            />
          </span>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function NavigationSettingsTab() {
  const { data: settings, isLoading, error } = useAdminNavigation();
  const updateMutation = useUpdateAdminNavigation();
  const resetMutation = useResetAdminNavigation();

  const [order, setOrder] = useState<string[]>([]);
  const [hidden, setHidden] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize from settings. Append any nav items that exist in the app but
  // aren't in the saved order yet (e.g. a newly-shipped item like Events that
  // predates the admin's saved navigation), so they show up here instead of
  // silently vanishing. Mirrors applyNavigationSettings in AdminLayout.
  useEffect(() => {
    if (settings) {
      const savedOrder = settings.order ?? [];
      const savedSet = new Set(savedOrder);
      const missing = Object.keys(NAV_ITEMS).filter((id) => !savedSet.has(id));
      setOrder([...savedOrder, ...missing]);
      setHidden(settings.hidden ?? []);
      setIsDirty(false);
    }
  }, [settings]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
      setIsDirty(true);
    }
  };

  const handleToggleVisibility = (itemId: string) => {
    if (ALWAYS_VISIBLE_ITEMS.includes(itemId)) return;
    setHidden((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    setIsDirty(true);
  };

  const handleSave = () => {
    updateMutation.mutate({ order, hidden });
    setIsDirty(false);
  };

  const handleReset = () => {
    if (window.confirm('Reset navigation to default order and visibility?')) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load navigation settings. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardHeader
            title="Reorder Menu"
            subheader="Drag to reorder and toggle visibility of navigation items"
            action={
              <SettingsWriteGate>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ResetIcon />}
                    onClick={handleReset}
                    disabled={resetMutation.isPending}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!isDirty || updateMutation.isPending}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </Stack>
              </SettingsWriteGate>
            }
          />
          <CardContent>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={order}
                strategy={verticalListSortingStrategy}
              >
                <List sx={{ bgcolor: 'background.paper' }}>
                  {order.map((itemId) => (
                    <SortableItem
                      key={itemId}
                      id={itemId}
                      isHidden={hidden.includes(itemId)}
                      onToggleVisibility={() => handleToggleVisibility(itemId)}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>

        <Alert severity="info" icon={false}>
          <Typography variant="body2">
            <strong>Tip:</strong> Drag items to reorder them in the sidebar. Toggle the switch to show or hide items.
            Changes are saved when you click "Save".
          </Typography>
        </Alert>
      </Stack>
    </Box>
  );
}
